<template>
  <div v-if="localShow" class="modal-overlay" @click="closeModal">
    <div class="modal-dialog" @click.stop>
      <BaseDialog
        :title="'Add Groups'"
        :width="'500px'"
        :info="'Datasets are split into 2 groups, training (train) and validation (val). This divides images into the 2 groups based on the percentages you set.'"
        :footerButtons="['Split']"
        @cancel="closeModal"
        @footer-click="handleFooterClick"
      >
        <div class="settings container scrollbar">
          <div class="row mt-3">
            <div class="input-label d-flex">
              <label class="label required">Groups</label>
            </div>
          </div>
          <div class="row mt-1">
            <v-checkbox
              v-model="unshuffledOnly"
              color="#250e81"
              :hide-details="true"
              :density="'compact'"
            >
              <template #label>
                <span class="checkbox-label">Only ungrouped images</span>
                <SVGIcon
                  v-tooltip="`If true, only split ungrouped images. This is useful when you want to maintain the split of current images.
                  If false, every image will be split into the selected percentages below.`"
                  :width="'20px'"
                  :height="'20px'"
                  :iconName="'info'"
                  class="help-icon"
                />
              </template>
            </v-checkbox>
          </div>
          <div v-if="dataset" class="row mt-3 custom-split-cards">
            <div class="col-5 settings__control-group custom-split-card _train">
              <label class="font-weight-bold">Training (train)</label>
              <p>{{ sliderValues ? `${sliderValues[0]} %` : '0 %' }}</p>
            </div>
            <div class="col-5 settings__control-group custom-split-card _validation">
              <label class="font-weight-bold">Validation (val)</label>
              <p>{{ sliderValues ? `${sliderValues[1]} %` : '0 %' }}</p>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col settings__control-group">
              <TrainValSplitSlider v-model="sliderValues" />
            </div>
          </div>

          <div v-if="message" class="row mt-3">
            <div class="col-12 settings__control-group">
              <div class="result error">
                <span>{{ message }}</span>
              </div>
            </div>
          </div>
        </div>
      </BaseDialog>
    </div>
  </div>
</template>

<script>
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import BaseDialog from '@/components/DilaogTemplate/BaseDialog.vue';
import { inputSanitizer, changeSanitizer } from '@/assets/js/utils';
import TrainValSplitSlider from '@/components/TrainValSplitSlider.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();
export default {
  name: 'CreateGroupsModal',
  components: {
    BaseDialog,
    TrainValSplitSlider,
    SVGIcon,
  },
  props: {
    dataset: {
      type: Object,
      default: null,
    },
  },
  emits: ['annotation-set-created', 'annotation-set-created-error', 'created-groups'],
  data() {
    return {
      localShow: false,
      message: '',
      sliderValues: [80, 20, 0],
      unshuffledOnly: true,
      groups: [
        {
          name: 'train',
          percent: 80,
        },
        {
          name: 'val',
          percent: 20,
        },
      ],
    };
  },
  watch: {
    sliderValues: {
      immediate: true,
      handler(newVals) {
        this.groups[0].percent = newVals[0];
        this.groups[1].percent = newVals[1];
      },
    },
  },
  created() {
    this.dataConnect = new DatastoreConnect(this.$store.state.enterpriseServerUrl);
  },
  methods: {
    inputSanitizer,
    changeSanitizer,
    showModal() {
      this.localShow = true;
    },
    resetData() {
      this.message = "";
      this.unshuffledOnly = true;
      this.sliderValues = [80, 20, 0];
      this.groups = [
        {
          name: 'train',
          percent: 80,
        },
        {
          name: 'val',
          percent: 20,
        },
      ];
    },
    closeModal() {
      this.resetData();
      this.localShow = false;
    },
    async createGroups() {
      if (this.message !== "") {
        return;
      }
      await this.dataConnect.createGroupsForDataset({
        dataset_id: this.dataset.id,
        group_names: this.groups.map((e) => e.name),
        group_splits: this.groups.map((e) => e.percent),
        unshuffled_only: this.unshuffledOnly,
      })
        .then((resp) => {
          if (resp.error !== undefined) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Error creating groups: ${resp.error.message}`,
              type: 'error',
              position: 'top',
            });
          }
          this.$emit('created-groups');
          this.closeModal();
        }).catch((e) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Error creating groups: ${e}`,
            type: 'error',
            position: 'top',
          });
        });
    },
  },
};

</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
}

.modal-dialog {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.settings {
  padding: 0.75rem 3rem;
  overflow-y: auto;
}

@media (min-width: $breakpoint-sm) {
  .settings {
    min-width: 500px;
  }
}

.checkbox-label {
  margin-left: 10px;
  font-size: 14px;
  font-weight: 600;
}

.custom-split-cards {
  display: flex;
  flex-direction: row;
  justify-content: center;
}

.custom-split-card {
  border-radius: 6px;
  padding: 10px;

  p {
    font-size: 0.9rem;
    font-weight: 700;
  }

  label {
    text-align: center !important;
  }
}

.custom-split-card + .custom-split-card {
  margin-left: 15px;
}

._train{
    border: 2px solid rgb(44, 145, 246);
}

._validation{
  @include themify {
    border: 2px solid themed('range-slider-selected-color');
  }
}

._test{
  @include themify {
    border: 2px solid themed('range-slider-selected-color-right');
  }
}

.result {
  display: flex;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  font-size: 1rem;
  font-weight:600;
  padding: 10px;
  align-items: center;
  justify-content: center;

  span {
    width: 100%;
    line-break: anywhere;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error')
  }
}

.note {
  text-align: left;
  font-size: 0.8rem;
  font-weight: 500;
}

.help-icon {
  margin: 0 5px;
  color: var(--color-primary);
}

.help-icon:focus {
  outline: none;
}

</style>

